import React, { useEffect, useState } from 'react';
import './Staff.css';
import { useTheme } from '../theme/ThemeContext';
import DataTable from 'react-data-table-component';
import LoadingSpinner from '../Loading/LoadingSpinner'; // Make sure to import this
import NotificationPopup from '../alertpopup/NotificationPopup'; // Make sure to import this

const Staff = () => {
  const token = localStorage.getItem('authToken')
  
  const backendurl = window.location.hostname === 'localhost'
  ? ''
  : window.location.hostname === 'demo.ollinwon.com'
    ? process.env.REACT_APP_BACKEND_URL
    : window.location.hostname === 'iot.aizoteq.com'
      ? process.env.REACT_APP_BACKEND_URL2
      : '';

  const { theme } = useTheme();
  const [data, setData] = useState([]);
  const [search, setSearch] = useState('');
  const [filteredData, setFilteredData] = useState([]);
  const [showSlide, setShowSlide] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState('admin');
  const [editData, setEditData] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [selectedRole, setSelectedRole] = useState('');
  
  // Loading states
  const [loading, setLoading] = useState({
    main: true,
    update: false
  });
  
  // Error state
  const [error, setError] = useState(null);
  
  // Notification state
  const [notification, setNotification] = useState({
    show: false,
    type: "success",
    message: "",
    title: ""
  });

  // Notification helper function
  const showNotification = (type, message, title = "", duration = 3000) => {
    setNotification({
      show: true,
      type,
      message,
      title
    });
    
    setTimeout(() => {
      setNotification(prev => ({ ...prev, show: false }));
    }, duration);
  };

  const handleEdit = (row) => {
    setEditData(row);
    setSelectedRole(row.userrole);
    setShowPopup(true);
  };

  const handleSlide = () => {
    setShowSlide((prev) => !prev);
  };

  const handleFilterChange = (filter) => {
    setSelectedFilter(filter);
    setShowSlide(false);
    fetchUsers(filter);
  };

  const fetchUsers = async (filter = selectedFilter, searchTerm = search) => {
    setLoading(prev => ({ ...prev, main: true }));
    setError(null);
    try {
      const endpoint = `${backendurl}/dashboard/api/display/users/${filter.toLowerCase()}?search=${searchTerm}`;
      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }
      
      const result = await response.json();
      
      // Check if data is empty
      if (!result.users || result.users.length === 0) {
        const message = `No ${filter} users found${searchTerm ? ` for search: ${searchTerm}` : ''}`;
        showNotification("info", message, "No Data");
      } else {
        showNotification("success", `Loaded ${result.users.length} ${filter} users`, "Success");
      }
      
      setData(result.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError(error.message);
      showNotification("error", error.message || "Failed to load users", "Error");
    } finally {
      setLoading(prev => ({ ...prev, main: false }));
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // Handle search with debouncing
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (search.trim() !== '') {
        fetchUsers(selectedFilter, search);
      } else {
        fetchUsers(selectedFilter, '');
      }
    }, 500); // 500ms delay

    return () => clearTimeout(delayDebounceFn);
  }, [search]);

  useEffect(() => {
    // Local filtering for the search functionality
    const filtered = data.filter((user) => {
      const searchTerm = search.toLowerCase();
      return (
        user.name?.toLowerCase().includes(searchTerm) ||
        user.username?.toLowerCase().includes(searchTerm) ||
        user.userrole?.toLowerCase().includes(searchTerm)
      );
    });
    setFilteredData(filtered);
    
    // Show notification if no results after local filtering
    if (!loading.main && search && filtered.length === 0) {
      showNotification("info", `No users found for "${search}"`, "No Results");
    }
  }, [search, data]);

  const handleSave = async () => {
    if (!editData) return;
    
    const allowedRoles = ['admin', 'staff', 'customer', 'dealer'];
    if (!allowedRoles.includes(selectedRole)) {
      showNotification("warning", `Invalid role selected. Allowed roles: ${allowedRoles.join(', ')}`, "Warning");
      return;
    }
  
    setLoading(prev => ({ ...prev, update: true }));
    
    try {
      const response = await fetch(`${backendurl}/dashboard/api/users/${editData.id}/role`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ userRole: selectedRole }),
        withCredentials: true,
      });
  
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update role');
      }
  
      const result = await response.json();
      
      // Update local state
      const updatedData = data.map((user) =>
        user.id === editData.id ? { ...user, userrole: selectedRole } : user
      );
      setData(updatedData);
      setFilteredData(updatedData);
      setShowPopup(false);
      
      showNotification("success", `User role updated to ${selectedRole}`, "Success");
    } catch (error) {
      console.error('Error updating user role:', error);
      showNotification("error", error.message || "Failed to update role", "Error");
    } finally {
      setLoading(prev => ({ ...prev, update: false }));
    }
  };

  const columns = [
    {
      name: 'ID',
      selector: (row, index) => index + 1,
      sortable: true,
    },
    {
      name: 'Image',
      selector: (row) => row.profilepic || 'N/A',
      sortable: true,
      cell: (row) => {
        const defaultProfilePic = `https://static-00.iconduck.com/assets.00/profile-user-icon-512x512-nm62qfu0.png`;
    
        return row.profilepic ? (
          <img
            src={row.profilepic.startsWith('http') ? row.profilepic : defaultProfilePic}
            alt="Profile"
            style={{ width: 40, height: 40, borderRadius: '50%' }}
            onError={(e) => {
              e.target.src = defaultProfilePic;
            }}
          />
        ) : 
        (
          <div
            style={{
              width: 40,
              height: 40,
              backgroundColor: '#ccc',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <span style={{ color: '#fff', fontSize: 14 }}>N/A</span>
          </div>
        );
      },
    },
    { name: 'Name', selector: (row) => row.name || 'N/A', sortable: true },
    { name: 'User Name', selector: (row) => row.username || 'N/A', sortable: true },
    { name: 'Role', selector: (row) => row.userrole || 'N/A', sortable: true },
    {
      name: 'Last Modified',
      selector: (row) => new Date(row.lastmodified).toLocaleString(),
      sortable: true,
    },
    {
      name: 'Action',
      cell: (row) => (
        <div className="device-action-btn">
          <button className="action-button" onClick={() => handleEdit(row)}>
            <i className="fa-solid fa-pen-to-square"></i>
          </button>
        </div>
      ),
    },
  ];

  // No data component
  const NoDataComponent = () => (
    <div style={{ 
      textAlign: 'center', 
      padding: '40px 20px',
      color: theme === 'dark' ? '#ccc' : '#666'
    }}>
      <i className="fa-solid fa-users" style={{ fontSize: '48px', marginBottom: '20px', opacity: 0.5 }}></i>
      <h3>No Users Found</h3>
      <p>{search ? `No users match your search "${search}"` : `No ${selectedFilter} users found`}</p>
    </div>
  );

  return (
    <div className={`user-main ${theme}-theme`}>
      {/* Notification Popup */}
      <NotificationPopup
        show={notification.show}
        type={notification.type}
        message={notification.message}
        title={notification.title}
        onClose={() => setNotification({ ...notification, show: false })}
        duration={5000}
      />
      
      <div className="dash-dummy"></div>
      <div className="second-main">
        <div className="user-content-main">
          <div className="user-content-nav">
            <div className="user-nav-head">
              <p>{selectedFilter.charAt(0).toUpperCase() + selectedFilter.slice(1)} Details</p>
              {loading.main && (
                <span style={{ 
                  marginLeft: '10px', 
                  fontSize: '0.9rem',
                  color: theme === 'dark' ? '#3498db' : '#2980b9'
                }}>
                  {/* <LoadingSpinner size="small" color={theme === 'dark' ? 'primary' : 'primary'} /> */}
                </span>
              )}
            </div>
            <div className="user-nav-items">
              <div className="device-search">
                <i className="fa-solid fa-magnifying-glass"></i>
                <input
                  type="search"
                  placeholder="Search..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  disabled={loading.main}
                />
                {loading.main && (
                  <div style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)' }}>
                    {/* <LoadingSpinner size="small" color={theme === 'dark' ? 'light' : 'dark'} /> */}
                  </div>
                )}
              </div>
              <div style={{ position: 'relative' }}>
                <i className="fa-solid fa-sliders" onClick={handleSlide}></i>
                {showSlide && (
                  <div className="customer-slide-menu show">
                    {['admin', 'staff', 'dealer', 'customer'].map((filter) => (
                      <div
                        key={filter}
                        className="thing-slide-item"
                        onClick={() => handleFilterChange(filter)}
                      >
                        <span>{filter}</span>
                        {selectedFilter === filter && <i className="fa-solid fa-check" style={{ marginLeft: '10px' }}></i>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {loading.main ? (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              height: '400px',
              width: '100%'
            }}>
              <LoadingSpinner 
                size="large" 
                text={`Loading ${selectedFilter} users...`} 
                color={theme === 'dark' ? 'primary' : 'primary'} 
              />
            </div>
          ) : error ? (
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column',
              justifyContent: 'center', 
              alignItems: 'center', 
              height: '400px',
              color: theme === 'dark' ? '#e74c3c' : '#c0392b'
            }}>
              <i className="fa-solid fa-triangle-exclamation" style={{ fontSize: '48px', marginBottom: '20px' }}></i>
              <h3>Error Loading Data</h3>
              <p>{error}</p>
              <button 
                onClick={() => fetchUsers()} 
                style={{
                  marginTop: '20px',
                  padding: '10px 20px',
                  backgroundColor: theme === 'dark' ? '#3498db' : '#2980b9',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer'
                }}
              >
                Retry
              </button>
            </div>
          ) : filteredData.length === 0 ? (
            <NoDataComponent />
          ) : (
            <DataTable 
              columns={columns} 
              data={filteredData} 
              pagination 
              noDataComponent={<NoDataComponent />}
            />
          )}
          
          {showPopup && (
            <div className="user-popup-overlay">
              <div className="user-popup-content">
                <button
                  className="user-popup-close"
                  type="button"
                  onClick={() => setShowPopup(false)}
                  disabled={loading.update}
                >
                  <i className="fa-solid fa-x"></i>
                </button>

                <h3>Edit User Role</h3>
                <p style={{ marginBottom: '20px', color: theme === 'dark' ? '#ccc' : '#666' }}>
                  Change role for {editData?.name || editData?.username}
                </p>
                
                {loading.update && (
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    marginBottom: '20px'
                  }}>
                    <LoadingSpinner size="small" text="Updating..." color="primary" />
                  </div>
                )}

                <div className="user-popup-dropdown">
                  <select
                    value={selectedRole}
                    onChange={(e) => setSelectedRole(e.target.value)}
                    disabled={loading.update}
                  >
                    <option value="admin">Admin</option>
                    <option value="staff">Staff</option>
                    <option value="dealer">Dealer</option>
                    <option value="customer">Customer</option>
                  </select>
                </div>

                <button 
                  type="button" 
                  className="user-popup-save" 
                  onClick={handleSave}
                  disabled={loading.update}
                >
                  {loading.update ? (
                    <LoadingSpinner size="small" color="white" />
                  ) : (
                    "Save Changes"
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Staff;